import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle, CreditCard, Crown, Sparkle, Lightning, Buildings,
  Receipt, Spinner, Calendar, ArrowsClockwise
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_VISUAL = {
  trial:      { icon: Sparkle,    color: 'border-zinc-300',     accent: 'text-zinc-600',     btnClass: '', label: 'Trial' },
  basic:      { icon: Lightning,  color: 'border-blue-500',     accent: 'text-blue-600',     btnClass: 'bg-blue-600 hover:bg-blue-700',       label: 'Basic' },
  pro:        { icon: Crown,      color: 'border-amber-500',    accent: 'text-amber-600',    btnClass: 'bg-amber-500 hover:bg-amber-600',     label: 'Pro' },
  enterprise: { icon: Buildings,  color: 'border-violet-500',   accent: 'text-violet-600',   btnClass: 'bg-violet-600 hover:bg-violet-700',   label: 'Enterprise' },
};

export default function BillingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [checkingOut, setCheckingOut] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [usage, setUsage] = useState(null);

  // Polling state when user returns from Stripe
  const sessionId = searchParams.get('session_id');
  const [pollStatus, setPollStatus] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, subRes, txnsRes, usageRes] = await Promise.all([
        axios.get(`${API_URL}/platform/subscription/plans`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/platform/subscription/status`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/billing/transactions`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/billing/usage`).catch(() => ({ data: null })),
      ]);
      const list = Array.isArray(plansRes.data) ? plansRes.data : (plansRes.data?.plans || []);
      setPlans(list);
      setCurrentSub(subRes.data);
      setTransactions(txnsRes.data || []);
      setUsage(usageRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load billing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Polling on return from Stripe checkout (per playbook)
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const max = 6;
    setPollStatus({ state: 'checking', message: 'Verifying payment…' });

    const tick = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API_URL}/billing/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === 'paid') {
          setPollStatus({ state: 'paid', message: `Payment successful! Your ${data.plan_id} plan is active.` });
          toast.success('Subscription activated!');
          await fetchData();
          // strip session_id from the url
          navigate('/dashboard/billing', { replace: true });
          return;
        }
        if (data.status === 'expired') {
          setPollStatus({ state: 'expired', message: 'Payment session expired.' });
          return;
        }
        if (attempts >= max) {
          setPollStatus({ state: 'pending', message: 'Payment is still processing. Refresh in a minute.' });
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        if (attempts >= max) {
          setPollStatus({ state: 'error', message: e.response?.data?.detail || 'Could not verify payment status.' });
          return;
        }
        setTimeout(tick, 2000);
      }
    };
    setTimeout(tick, 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleCheckout = async (planKey) => {
    setCheckingOut(planKey);
    try {
      const res = await axios.post(`${API_URL}/billing/checkout/session`, {
        plan_id: planKey,
        billing_cycle: billingCycle,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Stripe did not return a checkout URL');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setCheckingOut(null);
    }
  };

  const currentPlan = (currentSub?.plan || 'trial').toLowerCase();
  const yearlySavings = (m) => Math.round(m * 12 * 0.17);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Spinner size={28} className="animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Polling banner when returning from Stripe */}
      {pollStatus && (
        <div className={`p-3 rounded-xl border flex items-center gap-2 text-sm ${
          pollStatus.state === 'paid' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
          : pollStatus.state === 'error' || pollStatus.state === 'expired' ? 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-300'
          : 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300'
        }`} data-testid="billing-poll-banner">
          {pollStatus.state === 'checking' || pollStatus.state === 'pending'
            ? <Spinner size={16} className="animate-spin" />
            : <CheckCircle size={16} weight="fill" />}
          <span>{pollStatus.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <CreditCard size={26} weight="fill" className="text-[#d4a84b]" />
            Subscription &amp; Billing
          </h1>
          <p className="text-muted-foreground text-sm">Choose the plan that fits your team and unlock premium modules.</p>
        </div>
        {currentSub && (
          <div className="text-right text-xs text-muted-foreground" data-testid="current-sub-info">
            <div>Current plan</div>
            <div className="text-base font-semibold capitalize text-foreground">
              {currentSub.plan || currentPlan}
              <Badge variant="outline" className="ml-2 capitalize">{currentSub.status || 'trial'}</Badge>
            </div>
            {currentSub.current_period_end && (
              <div className="mt-1 flex items-center gap-1 justify-end text-[11px]">
                <Calendar size={12} /> Renews {new Date(currentSub.current_period_end).toLocaleDateString()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Plan-quota usage tiles (Trial only) */}
      {usage && (usage.quotations?.cap || usage.invoices?.cap) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="usage-tiles">
          {[
            { label: 'Quotations this month', used: usage.quotations?.used, cap: usage.quotations?.cap, color: 'amber' },
            { label: 'Invoices this month',   used: usage.invoices?.used,   cap: usage.invoices?.cap,   color: 'rose' },
            { label: 'Active staff',          used: usage.users?.used,      cap: usage.users?.cap,      color: 'blue' },
          ].map((m) => {
            const pct = m.cap > 0 ? Math.min(100, Math.round((m.used / m.cap) * 100)) : 0;
            const danger = pct >= 80;
            const colorBg = m.color === 'rose' ? 'bg-rose-500' : m.color === 'amber' ? 'bg-amber-500' : 'bg-blue-500';
            return (
              <div key={m.label} className="bg-card border rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">{m.label}</span>
                  <Badge variant="outline" className={danger ? 'text-red-600 border-red-300' : ''}>
                    {m.used} / {m.cap === -1 ? '∞' : m.cap}
                  </Badge>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full ${colorBg} transition-all`} style={{ width: `${pct}%` }} />
                </div>
                {danger && (
                  <p className="text-xs text-red-600 mt-2">Approaching plan limit — consider upgrading to Basic.</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cycle toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-full bg-muted p-1 text-sm">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-5 py-1.5 rounded-full transition-all ${billingCycle === 'monthly' ? 'bg-card shadow font-semibold' : 'text-muted-foreground'}`}
            data-testid="cycle-monthly-btn"
          >Monthly</button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-5 py-1.5 rounded-full transition-all flex items-center gap-2 ${billingCycle === 'yearly' ? 'bg-card shadow font-semibold' : 'text-muted-foreground'}`}
            data-testid="cycle-yearly-btn"
          >
            Yearly
            <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-[10px]" variant="outline">Save 17%</Badge>
          </button>
        </div>
      </div>

      {/* Plan grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plans-grid">
        {plans.map((plan) => {
          const key = (plan.id || plan.plan || '').toLowerCase();
          const visual = PLAN_VISUAL[key] || PLAN_VISUAL.basic;
          const Icon = visual.icon;
          const monthly = Number(plan.price_monthly || 0);
          const display = billingCycle === 'monthly' ? monthly : Math.round(monthly * 12 * 0.83);
          const isCurrent = currentPlan === key;
          const features = plan.features || {};
          const featureList = [
            features.hr && 'HR & Employees',
            features.payroll && 'Payroll',
            features.sales && 'Sales (Quotes & Invoices)',
            features.purchases && 'Purchases',
            features.accounts && 'Accounts',
            features.inventory && 'Inventory',
            features.analytics && 'Analytics',
            features.forecasting && 'Forecasting',
            features.pdf_customization && 'PDF Customization',
            features.api_access && 'API Access',
            features.white_label && 'White-label',
            features.priority_support && 'Priority Support',
          ].filter(Boolean);
          const maxUsers = plan.max_users === -1 ? 'Unlimited users' : `Up to ${plan.max_users} users`;

          return (
            <Card key={key} className={`relative p-5 border-2 ${visual.color} ${isCurrent ? 'ring-2 ring-[#d4a84b] ring-offset-2 ring-offset-background' : ''}`}
              data-testid={`plan-card-${key}`}>
              {isCurrent && <Badge className="absolute -top-3 right-4 bg-[#d4a84b] text-black border-0">Current</Badge>}
              <div className="flex items-center gap-2 mb-2">
                <Icon size={24} weight="fill" className={visual.accent} />
                <h2 className="text-lg font-bold">{visual.label}</h2>
              </div>
              <div className="mb-3">
                {monthly === 0 ? (
                  <div className="text-3xl font-extrabold">Free</div>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-extrabold">${display}</span>
                      <span className="text-sm text-muted-foreground">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                    </div>
                    {billingCycle === 'yearly' && monthly > 0 && (
                      <div className="text-xs text-emerald-600 mt-0.5">Save ${yearlySavings(monthly)} per year</div>
                    )}
                  </>
                )}
              </div>
              <div className="text-xs text-muted-foreground mb-3">{maxUsers}</div>
              <ul className="text-sm space-y-1.5 mb-5 min-h-[180px]">
                {featureList.map((f) => (
                  <li key={f} className="flex items-start gap-1.5">
                    <CheckCircle size={14} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {key === 'trial' || monthly === 0 ? (
                <Button variant="outline" className="w-full" disabled data-testid={`subscribe-${key}-btn`}>Free</Button>
              ) : isCurrent ? (
                <Button variant="outline" className="w-full" disabled data-testid={`subscribe-${key}-btn`}>Active</Button>
              ) : (
                <Button
                  className={`w-full ${visual.btnClass} text-white`}
                  onClick={() => handleCheckout(key)}
                  disabled={checkingOut === key}
                  data-testid={`subscribe-${key}-btn`}
                >
                  {checkingOut === key
                    ? <Spinner size={14} className="animate-spin mr-1" />
                    : <CreditCard size={14} className="mr-1" />}
                  {checkingOut === key ? 'Redirecting…' : `Upgrade to ${visual.label}`}
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      {/* Payment History */}
      <div className="bg-card border rounded-xl overflow-hidden" data-testid="billing-history-section">
        <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt size={18} weight="fill" className="text-muted-foreground" />
            <h2 className="font-semibold">Payment History</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData} title="Refresh" data-testid="refresh-history-btn">
            <ArrowsClockwise size={14} />
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/30 text-muted-foreground text-xs uppercase">
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Plan</th>
                <th className="text-left px-4 py-2">Cycle</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Session</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">No payment history yet.</td></tr>
              ) : transactions.map((t) => (
                <tr key={t.id || t.session_id} className="border-t hover:bg-muted/20" data-testid={`txn-${t.session_id}`}>
                  <td className="px-4 py-2">{t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</td>
                  <td className="px-4 py-2 capitalize">{t.plan_id}</td>
                  <td className="px-4 py-2 capitalize">{t.billing_cycle}</td>
                  <td className="px-4 py-2 text-right font-mono">${t.amount?.toFixed(2)} {t.currency?.toUpperCase()}</td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className={
                      t.payment_status === 'paid' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
                      : t.payment_status === 'pending' || t.status === 'initiated' ? 'bg-amber-500/10 text-amber-600 border-amber-500/30'
                      : 'bg-zinc-500/10 text-zinc-600'
                    }>{t.payment_status || t.status}</Badge>
                  </td>
                  <td className="px-4 py-2 hidden md:table-cell font-mono text-xs text-muted-foreground truncate max-w-[180px]">{t.session_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
